import { useState, useMemo, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { propOr } from 'ramda';
import OnboardingContentWrapper from 'components/OnboardingContentWrapper';

/* Styles */
import styles from 'styles/login.module.scss';
import layoutStyles from 'styles/layout.module.scss';
import cssVars from 'styles/vars.module.scss';

/* i18n */
import { useIntl } from 'react-intl';

/* API */
import { apiRequest } from 'lib/API';
import { useQuery, useQueryClient } from '@tanstack/react-query';

/* Material UI */
import Typography from '@mui/material/Typography';
import OnboardingSteps from 'components/OnboardingSteps';
import OnboardingNavigationButtons from 'components/OnboardingNavigationButtons';
import {
  Box,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  MenuItem,
  Select,
  FormHelperText,
  FormLabel,
  InputLabel,
} from '@mui/material';
const validationSchema = yup.object({
  military_affiliated: yup.string().required('forms.onboarding.eligibility.militaryServiceRequired'),
  military_relationship: yup.string().required('forms.onboarding.eligibility.militaryRelationshipRequired'),
  education_level: yup.string().required('forms.onboarding.eligibility.educationLevelRequired'),
});

// Required field indicator component
const RequiredAsterisk = ({ cssVars }) => <span style={{ color: propOr('red', 'errorColorMain', cssVars) }}>*</span>;

export default function OnboardingEligibility() {
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(location.state?.step === 2 ? 2 : 1);
  const [experienceType, setExperienceType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const urlPrefix = intl.locale === 'en' ? '' : `/${intl.locale}`;

  // Read cached experience data for pre-population on back navigation
  const experienceQuery = useQuery({
    queryKey: ['/users/me/experience/'],
    enabled: false, // Don't fetch, just read from cache
  });
  const expData = experienceQuery.data;

  const educationOptions = useMemo(
    () => [
      {
        value: '11',
        title: intl.formatMessage({ id: 'forms.onboarding.education.noDiploma' }),
        id: 'less-than-hs-eligibility-dropdown',
      },
      {
        value: '11.5',
        title: intl.formatMessage({ id: 'forms.onboarding.education.ged' }),
        id: 'ged-eligibility-dropdown',
      },
      {
        value: '12',
        title: intl.formatMessage({ id: 'forms.onboarding.education.highSchoolDiploma' }),
        id: 'diploma-eligibility-dropdown',
      },
      {
        value: '13',
        title: intl.formatMessage({ id: 'forms.onboarding.education.someCollege' }),
        id: 'some-college-eligibility-dropdown',
      },
      {
        value: '15',
        title: intl.formatMessage({ id: 'forms.onboarding.education.certificateProficiency' }),
        id: 'cert-proficiency-eligibility-dropdown',
      },
      {
        value: '18',
        title: intl.formatMessage({ id: 'forms.onboarding.education.technicalCertificate' }),
        id: 'technical-cert-eligibility-dropdown',
      },
      {
        value: '14',
        title: intl.formatMessage({ id: 'forms.onboarding.education.assocDegree' }),
        id: 'associates-eligibility-dropdown',
      },
      {
        value: '19',
        title: intl.formatMessage({ id: 'forms.onboarding.education.advancedCertificate' }),
        id: 'advanced-cert-eligibility-dropdown',
      },
      {
        value: '16',
        title: intl.formatMessage({ id: 'forms.onboarding.education.bachDegree' }),
        id: 'bachelors-eligibility-dropdown',
      },
      {
        value: '21',
        title: intl.formatMessage({ id: 'forms.onboarding.education.postBachelorsCertificate' }),
        id: 'post-bachelors-eligibility-dropdown',
      },
      {
        value: '17',
        title: intl.formatMessage({ id: 'forms.onboarding.education.mastersDegree' }),
        id: 'masters-eligibility-dropdown',
      },
      {
        value: '16.5',
        title: intl.formatMessage({ id: 'forms.onboarding.education.graduateCertificate' }),
        id: 'grad-cert-eligibility-dropdown',
      },
      {
        value: '22',
        title: intl.formatMessage({ id: 'forms.onboarding.education.doctoralProfessional' }),
        id: 'doctoral-professional-eligibility-dropdown',
      },
      {
        value: '20',
        title: intl.formatMessage({ id: 'forms.onboarding.education.doctoralResearch' }),
        id: 'doctoral-research-eligibility-dropdown',
      },
    ],
    [intl]
  );

  // Derive initial values from cached experience data (for back navigation)
  const getInitialMilitaryAffiliated = () => {
    if (expData?.current_military_service) return 'currently_enlisted';
    if (expData?.previous_military_service) return 'previously_enlisted';
    if (expData?.current_military_service === false && expData?.previous_military_service === false) return 'no';
    return '';
  };

  const formik = useFormik({
    initialValues: {
      military_affiliated: getInitialMilitaryAffiliated(),
      military_relationship:
        expData?.is_military_family_member === true ? 'yes' : expData?.is_military_family_member === false ? 'no' : '',
      education_level: String(expData?.education?.[0]?.degree || ''),
    },
    enableReinitialize: !!expData,
    validationSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      // Save eligibility data to backend
      const payload = {
        data: {
          job_experience: [],
          previous_military_service: values.military_affiliated === 'previously_enlisted',
          current_military_service: values.military_affiliated === 'currently_enlisted',
          is_military_family_member: values.military_relationship === 'yes',
          education: [{ degree: values.education_level }],
        },
      };

      // Debug logging (development only)
      if (process.env.NODE_ENV === 'development') {
        console.log('Sending eligibility data:', JSON.stringify(payload, null, 2));
      }

      setIsSubmitting(true);
      apiRequest('PUT', '/users/me/experience/', payload)
        .then(() => {
          // Update cache immediately so back navigation can pre-populate
          queryClient.setQueryData(['/users/me/experience/'], (old) => ({
            ...old,
            ...payload.data,
          }));
          setIsSubmitting(false);
          // Move to step 2 after successful save
          setStep(2);
        })
        .catch((error) => {
          setIsSubmitting(false);
          console.error('Error saving eligibility data:', error);
          // Continue to step 2 even if API fails to not block user
          setStep(2);
        });
    },
  });

  const handleExperienceTypeSubmit = useCallback(() => {
    if (!experienceType) return;

    const hasMilitaryService = formik.values.military_affiliated !== 'no';

    if (experienceType === 'work') {
      const path = hasMilitaryService ? '/onboarding/military-experience' : '/onboarding/work-experience';
      history.push(`${urlPrefix}${path}`);
    } else if (experienceType === 'education') {
      history.push(`${urlPrefix}/onboarding/education`);
    }
  }, [experienceType, history, urlPrefix, formik.values.military_affiliated]);

  // Step 1: Questions
  if (step === 1) {
    return (
      <Box sx={{ boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.1)' }}>
        <div className={styles.loginFlow__form__onboarding}>
          <Box px={3}>
            <Typography align="center" variant="h1" gutterBottom sx={{ fontSize: 24, mt: 1 }}>
              {intl.formatMessage({ id: 'onboarding.title' }, { step: 2, totalSteps: 5 })}
            </Typography>
          </Box>
          <Box sx={{ maxWidth: 222, marginLeft: 'auto', marginRight: 'auto', mt: 4, mb: 1 }}>
            <OnboardingSteps activeStep={2} />
          </Box>
        </div>
        <hr className={layoutStyles.separatorBold} />
        <Box className={styles.loadingScreen__cardsContainer}>
          <OnboardingContentWrapper>
            <Typography
              align="center"
              variant="h5"
              component="h2"
              gutterBottom
              px={{ xs: 1, md: 6 }}
              sx={{ color: 'rgb(66, 66, 66)', mb: 3 }}
            >
              {intl.formatMessage({ id: 'forms.onboarding.eligibility.introText' })}
            </Typography>

            <form onSubmit={formik.handleSubmit}>
              {/* Military Affiliation Question */}
              <Box sx={{ mb: 0.5 }}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  {intl.formatMessage({ id: 'forms.onboarding.eligibility.militaryService' })}
                  <RequiredAsterisk cssVars={cssVars} />
                </Typography>
                <FormControl
                  error={!!formik.touched.military_affiliated && !!formik.errors.military_affiliated}
                  fullWidth
                >
                  <RadioGroup
                    name="military_affiliated"
                    value={formik.values.military_affiliated}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    row
                  >
                    <FormControlLabel
                      value="currently_enlisted"
                      control={<Radio color="primary" />}
                      label={intl.formatMessage({ id: 'forms.onboarding.eligibility.currentlyEnlisted' })}
                    />
                    <FormControlLabel
                      value="previously_enlisted"
                      control={<Radio color="primary" />}
                      label={intl.formatMessage({ id: 'forms.onboarding.eligibility.previouslyEnlisted' })}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio color="primary" />}
                      label={intl.formatMessage({ id: 'jobSearchActivity.form.no' })}
                    />
                  </RadioGroup>
                  {formik.touched.military_affiliated && formik.errors.military_affiliated && (
                    <FormHelperText>{intl.formatMessage({ id: formik.errors.military_affiliated })}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              {/* Military Relationship Question */}
              <Box sx={{ mb: 0.5 }}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  {intl.formatMessage({ id: 'forms.onboarding.eligibility.militaryRelationship' })}
                  <RequiredAsterisk cssVars={cssVars} />
                </Typography>
                <FormControl
                  error={!!formik.touched.military_relationship && !!formik.errors.military_relationship}
                  fullWidth
                >
                  <RadioGroup
                    name="military_relationship"
                    value={formik.values.military_relationship}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    row
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio color="primary" />}
                      label={intl.formatMessage({ id: 'jobSearchActivity.form.yes' })}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio color="primary" />}
                      label={intl.formatMessage({ id: 'jobSearchActivity.form.no' })}
                    />
                  </RadioGroup>
                  {formik.touched.military_relationship && formik.errors.military_relationship && (
                    <FormHelperText>{intl.formatMessage({ id: formik.errors.military_relationship })}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              {/* Education Level Dropdown */}
              <Box sx={{ mb: 0.5 }}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  {intl.formatMessage({ id: 'forms.onboarding.eligibility.educationLevel' })}
                  <RequiredAsterisk cssVars={cssVars} />
                </Typography>
                <FormControl
                  fullWidth
                  variant="filled"
                  error={!!formik.touched.education_level && !!formik.errors.education_level}
                >
                  <InputLabel id="education-level-label" htmlFor="education-level-input-id">
                    {intl.formatMessage({ id: 'forms.onboarding.eligibility.educationLevelLabel' })}
                  </InputLabel>
                  <Select
                    name="education_level"
                    labelId="education-level-label"
                    value={formik.values.education_level}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    inputProps={{
                      id: 'education-level-input-id',
                    }}
                  >
                    {educationOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value} id={option.id}>
                        {option.title}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.education_level && formik.errors.education_level && (
                    <FormHelperText>{intl.formatMessage({ id: formik.errors.education_level })}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              {/* Navigation Buttons */}
              <OnboardingNavigationButtons
                onBack={() => history.push(`${urlPrefix}/onboarding/contact-info`)}
                onNext={formik.handleSubmit}
                nextDisabled={!formik.isValid || isSubmitting}
                loading={isSubmitting}
                backId="back-onboard-eligibility-button"
                nextId="next-onboard-eligibility-button"
              />
            </form>
          </OnboardingContentWrapper>
        </Box>
      </Box>
    );
  }

  // Step 2: Experience Type Selection
  return (
    <Box sx={{ boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.1)' }}>
      <div className={styles.loginFlow__form__onboarding}>
        <Box px={3}>
          <Typography align="center" variant="h1" gutterBottom sx={{ fontSize: 24, mt: 1 }}>
            {intl.formatMessage({ id: 'onboarding.title' }, { step: 3, totalSteps: 5 })}
          </Typography>
        </Box>
        <Box sx={{ maxWidth: 222, marginLeft: 'auto', marginRight: 'auto', mt: 4, mb: 1 }}>
          <OnboardingSteps activeStep={3} />
        </Box>
      </div>
      <hr className={layoutStyles.separatorBold} />

      <Box className={styles.loadingScreen__cardsContainer}>
        <OnboardingContentWrapper>
          <Box>
            <Typography
              align="center"
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ color: 'rgb(66, 66, 66)', mb: 3 }}
            >
              {intl.formatMessage({ id: 'forms.onboarding.eligibility.experienceIntro1' })}
            </Typography>
          </Box>

          <FormControl component="fieldset" fullWidth sx={{ px: '16px' }}>
            <FormLabel
              component="legend"
              sx={{
                mb: 1,
                fontWeight: 600,
                color: 'text.primary',
                '&.Mui-focused': {
                  color: 'text.primary',
                },
              }}
            >
              {intl.formatMessage({ id: 'forms.onboarding.eligibility.experienceQuestion' })}
              <RequiredAsterisk cssVars={cssVars} />
            </FormLabel>
            <Typography variant="body2" sx={{ color: 'rgb(66, 66, 66)', mt: 0.5, mb: 2 }}>
              {intl.formatMessage(
                { id: 'forms.onboarding.eligibility.experienceIntro2' },
                { b: (chunks) => <strong style={{ textDecoration: 'underline' }}>{chunks}</strong> }
              )}
            </Typography>

            <RadioGroup value={experienceType} onChange={(e) => setExperienceType(e.target.value)}>
              <FormControlLabel
                value="education"
                control={<Radio />}
                label={intl.formatMessage({ id: 'forms.onboarding.eligibility.educationExperience' })}
                sx={{ mb: 0 }}
              />
              <FormControlLabel
                value="work"
                control={<Radio />}
                label={intl.formatMessage({ id: 'forms.onboarding.eligibility.workExperience' })}
                sx={{ mb: 0 }}
              />
            </RadioGroup>
          </FormControl>

          <OnboardingNavigationButtons
            onBack={() => setStep(1)}
            onNext={handleExperienceTypeSubmit}
            nextDisabled={!experienceType}
            backId="back-experience-type-button"
            nextId="next-experience-type-button"
          />
        </OnboardingContentWrapper>
      </Box>
    </Box>
  );
}
